/**
 * Console admin du Live School : épingle un exercice riche (≈10 questions) par
 * défi à venir, au lieu de l'auto-sélection. Agit sur
 * `live_challenges.source_exercise_id` via l'edge fn `live-admin-set-exercise`
 * (qui reconstruit le payload et renvoie le nb d'étapes obtenu).
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { Pencil, Play } from 'lucide-react';
import { SCHOOL_LEVELS } from '@jeraisonne/db';
import { countLiveSteps, weekStartOf, type LiveSourceBlock } from '@jeraisonne/db/live';
import { Button, Card } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { isoToLocalInput, localInputToIso } from '@/lib/live-campaigns';
import { groupChallengesByCampaign } from './group-challenges';
import { ExercisePickerField } from '@/components/pickers/exercise-picker';

// Tables live + colonnes postérieures aux types stale → client générique.
const db = supabase as unknown as SupabaseClient;

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const dateFmt = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

interface ChallengeRow {
  id: string;
  level: string;
  starts_at: string;
  status: string;
  step_count: number;
  encrypted_payload: string | null;
  source_exercise_id: string | null;
  campaign_id: string | null;
  campaignTitle: string | null;
  sourceTitle: string | null;
}

interface RawChallengeRow {
  id: string;
  level: string;
  starts_at: string;
  status: string;
  step_count: number;
  encrypted_payload: string | null;
  source_exercise_id: string | null;
  campaign_id: string | null;
  campaign: { id: string; title: string } | null;
}

export function LiveAdminPanel() {
  const challengesQuery = useQuery({
    queryKey: ['live-admin-challenges'],
    queryFn: async (): Promise<ChallengeRow[]> => {
      const { data } = await db
        .from('live_challenges')
        .select(
          'id, level, week_start, starts_at, status, step_count, encrypted_payload, source_exercise_id, campaign_id, campaign:live_campaigns(id, title)',
        )
        .in('status', ['scheduled', 'live'])
        // Les défis privés (0650) se gèrent par leur organisateur.
        .eq('visibility', 'public')
        .order('starts_at', { ascending: true });
      const rows = ((data as RawChallengeRow[] | null) ?? []).slice();
      const srcIds = [
        ...new Set(rows.map((r) => r.source_exercise_id).filter(Boolean)),
      ] as string[];
      let titles: Record<string, string> = {};
      if (srcIds.length) {
        const { data: exs } = await db.from('exercises').select('id, title').in('id', srcIds);
        titles = Object.fromEntries(
          ((exs as Array<{ id: string; title: string }> | null) ?? []).map((e) => [e.id, e.title]),
        );
      }
      return rows.map((r) => ({
        id: r.id,
        level: r.level,
        starts_at: r.starts_at,
        status: r.status,
        step_count: r.step_count,
        encrypted_payload: r.encrypted_payload,
        source_exercise_id: r.source_exercise_id,
        campaign_id: r.campaign_id,
        campaignTitle: r.campaign?.title ?? null,
        sourceTitle: r.source_exercise_id ? (titles[r.source_exercise_id] ?? null) : null,
      }));
    },
  });

  if (challengesQuery.isLoading) {
    return <p className="text-ink-500 text-sm">Chargement des défis…</p>;
  }
  const rows = challengesQuery.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-ink-500 text-sm">
        Aucun défi programmé. Les défis sont matérialisés par les campagnes actives (cron
        <code className="text-ink-700"> live-campaign-seed</code>) — crée ou active une campagne
        dans l’onglet « Campagnes ».
      </p>
    );
  }

  const groups = groupChallengesByCampaign(rows);

  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.campaignId ?? '__none__'} className="space-y-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-display text-ink-900 text-sm font-bold tracking-tight">
              {group.title ?? 'Hors campagne'}
            </h2>
            <span className="text-ink-400 text-xs">
              {group.rows.length} défi{group.rows.length > 1 ? 's' : ''}
            </span>
          </div>
          {group.rows.map((row) => (
            <ChallengeCard key={row.id} row={row} />
          ))}
        </section>
      ))}
    </div>
  );
}

function ChallengeCard({ row }: { row: ChallengeRow }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState('');
  const [selectedSteps, setSelectedSteps] = useState<number | null>(null);
  const [editDate, setEditDate] = useState(false);
  const [dateValue, setDateValue] = useState('');

  // Le défi n'est éditable (date) que tant qu'il est `scheduled` : une fois en
  // direct, changer l'heure de départ n'a plus de sens (les clés sont libérées).
  const canEditDate = row.status === 'scheduled';

  // Changer `starts_at` ne nécessite PAS de reconstruire le payload chiffré (les
  // clés sont dérivées par étape et libérées au fil de l'horloge serveur). On
  // recalcule juste `week_start` (dénormalisé, ancre des classements/IR).
  const dateMutation = useMutation({
    mutationFn: async (iso: string) => {
      const { error } = await db
        .from('live_challenges')
        .update({ starts_at: iso, week_start: weekStartOf(iso) })
        .eq('id', row.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-admin-challenges'] });
      setEditDate(false);
    },
  });

  function startEditDate() {
    setDateValue(isoToLocalInput(row.starts_at));
    dateMutation.reset();
    setEditDate(true);
  }

  const exercisesQuery = useQuery({
    queryKey: ['live-admin-exercises', row.level],
    enabled: open,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data } = await db
        .from('exercises')
        .select('id, title, chapters!inner(level)')
        .eq('status', 'published')
        .eq('content_version', 'v2')
        .eq('chapters.level', row.level)
        .order('title')
        .limit(300);
      return ((data as Array<{ id: string; title: string }> | null) ?? []).map((e) => ({
        id: e.id,
        title: e.title,
      }));
    },
  });

  async function onPick(exId: string) {
    setSelected(exId);
    setSelectedSteps(null);
    if (!exId) return;
    const { data } = await db
      .from('exercise_blocks')
      .select('id, parent_id, block_type, section, position, data')
      .eq('exercise_id', exId);
    setSelectedSteps(countLiveSteps((data as LiveSourceBlock[] | null) ?? [], 10));
  }

  const setMutation = useMutation({
    mutationFn: async (exerciseId: string | null) => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        build?: { step_count?: number };
      }>('live-admin-set-exercise', { body: { challenge_id: row.id, exercise_id: exerciseId } });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['live-admin-challenges'] });
      setOpen(false);
      setSelected('');
      setSelectedSteps(null);
    },
  });

  const built = row.encrypted_payload != null;

  return (
    <Card className="p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-ink-900 text-base font-bold">
            {LEVEL_LABEL[row.level] ?? row.level}
            {row.status === 'live' && (
              <span className="bg-error-500 ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold text-white">
                EN DIRECT
              </span>
            )}
          </p>
          {editDate ? (
            <div className="mt-1 space-y-2">
              <input
                type="datetime-local"
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
                className="border-ink-200 min-h-11 w-full rounded-xl border bg-white px-3 text-sm"
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  disabled={!localInputToIso(dateValue) || dateMutation.isPending}
                  onClick={() => {
                    const iso = localInputToIso(dateValue);
                    if (iso) dateMutation.mutate(iso);
                  }}
                >
                  {dateMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={dateMutation.isPending}
                  onClick={() => setEditDate(false)}
                >
                  Annuler
                </Button>
              </div>
              {dateMutation.isError && (
                <p className="text-error-600 text-xs">
                  Impossible d’enregistrer ce créneau (peut-être déjà pris pour ce niveau). Choisis
                  une autre date/heure.
                </p>
              )}
            </div>
          ) : (
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className="text-ink-500 text-sm">
                {dateFmt.format(new Date(row.starts_at))}
              </span>
              {canEditDate && (
                <button
                  type="button"
                  onClick={startEditDate}
                  className="text-primary-600 min-h-11 text-xs font-medium hover:underline"
                >
                  Modifier la date
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-right">
          <p className="text-ink-700 text-sm">
            {row.sourceTitle ? (
              <>
                <span className="font-medium">{row.sourceTitle}</span>
              </>
            ) : (
              <span className="text-ink-400 italic">Auto-sélection</span>
            )}
          </p>
          <p className="text-ink-400 text-xs">
            {built ? `${row.step_count} questions · payload prêt` : 'payload non construit'}
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setOpen((o) => !o)}>
          {open ? 'Fermer' : 'Choisir un exercice'}
        </Button>
        {row.source_exercise_id && (
          <>
            <a
              href={`/tutor/exercises/${row.source_exercise_id}`}
              target="_blank"
              rel="noreferrer"
              className="border-ink-200 text-ink-800 hover:bg-ink-50 inline-flex min-h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-sm font-medium transition-colors"
            >
              <Play className="h-3.5 w-3.5" />
              Tester
            </a>
            <a
              href={`/tutor/exercises/${row.source_exercise_id}/edit-v2`}
              target="_blank"
              rel="noreferrer"
              className="border-ink-200 text-ink-800 hover:bg-ink-50 inline-flex min-h-9 items-center gap-1.5 rounded-lg border bg-white px-3 text-sm font-medium transition-colors"
            >
              <Pencil className="h-3.5 w-3.5" />
              Modifier
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={setMutation.isPending}
              onClick={() => setMutation.mutate(null)}
            >
              Repasser en auto
            </Button>
          </>
        )}
      </div>

      {open && (
        <div className="border-ink-100 mt-3 space-y-3 rounded-xl border bg-white p-3">
          {exercisesQuery.isLoading ? (
            <p className="text-ink-500 text-sm">Chargement des exercices…</p>
          ) : (
            <>
              <ExercisePickerField
                label={`Exercice V2 publié (${LEVEL_LABEL[row.level] ?? row.level})`}
                items={(exercisesQuery.data ?? []).map((e) => ({
                  id: e.id,
                  title: e.title,
                  level: row.level,
                }))}
                value={selected || null}
                onChange={onPick}
                title="Choisir l’exercice du défi"
              />

              {selectedSteps != null && (
                <p
                  className={`text-sm font-medium ${selectedSteps >= 10 ? 'text-success-600' : selectedSteps >= 3 ? 'text-ink-600' : 'text-error-600'}`}
                >
                  ≈ {selectedSteps} question{selectedSteps > 1 ? 's' : ''} jouable
                  {selectedSteps > 1 ? 's' : ''}
                  {selectedSteps < 3 && ' — insuffisant (min. 3)'}
                </p>
              )}

              {setMutation.isError && (
                <p className="text-error-600 text-sm">
                  Échec : cet exercice ne fournit pas assez de questions. Choisis-en un autre.
                </p>
              )}

              <Button
                type="button"
                size="sm"
                fullWidth
                disabled={
                  !selected || (selectedSteps != null && selectedSteps < 3) || setMutation.isPending
                }
                onClick={() => setMutation.mutate(selected)}
              >
                {setMutation.isPending ? 'Reconstruction…' : 'Définir & reconstruire'}
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
